<script>
import { useVuelidate } from '@vuelidate/core';
import { helpers, requiredIf } from '@vuelidate/validators';
import { useAutomationLocales } from 'dashboard/routes/dashboard/settings/wgptAutomations/composables/useAutomationLocales';
import ButtonNext from 'next/button/Button.vue';
import IconNext from 'next/icon/Icon.vue';
import VariablesDropdown from '../variablesDropdown/Index.vue';
import variablesMixins from '../variablesDropdown/variablesMixins';
import { resolveVariablePath } from '../../helpers/variablePathResolver';

export default {
  components: {
    VariablesDropdown,
    ButtonNext,
    IconNext,
  },
  mixins: [variablesMixins],
  props: {
    value: {
      type: [String, Number, Date, Boolean, Object, Array],
      default: undefined,
    },
    defaultValue: {
      type: [String, Number, Date, Boolean, Object, Array],
      default: undefined,
    },
    valueType: {
      type: String,
      default: 'string',
    },
    label: {
      type: String,
      default: '',
    },
    fallbackLabel: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      required: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    dirty: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['change'],
  setup() {
    const { toLocale } = useAutomationLocales();

    return {
      v$: useVuelidate(),
      toLocale,
    };
  },
  data() {
    return {
      updatedValue: this.value ?? this.defaultValue ?? '',
    };
  },
  computed: {
    defaultOptionLabel() {
      if (this.placeholder) return this.placeholder;
      if (this.required)
        return this.$t(
          'WGPT_AUTOMATIONS.EDITOR.SIDEBAR.DYNAMIC_FIELDS.SELECT.DEFAULT_OPTION_REQUIRED'
        );
      return this.$t(
        'WGPT_AUTOMATIONS.EDITOR.SIDEBAR.DYNAMIC_FIELDS.SELECT.DEFAULT_OPTION'
      );
    },
    options() {
      return this.variables
        .filter(variable => !variable.wrapper)
        .map(variable => {
          const label = this.toLocale(variable.label, variable.name);
          const value = variable.name;
          return { label, value };
        });
    },
    // Resolves the selected dotted path through wrappers; custom objects can
    // nest other custom objects (e.g. car_listing.make.name).
    resolvedVariableChain() {
      if (!this.updatedValue || !String(this.updatedValue).includes('.')) {
        return null;
      }
      return resolveVariablePath(this.variables, this.updatedValue, {
        getCustomObjects:
          this.$store.getters['wgptAutomationRegistry/getCustomObjects'],
        getDynamicOptions:
          this.$store.getters['wgptAutomationRegistry/getDynamicOptions'],
        toLocale: this.toLocale,
      });
    },
    variableExists() {
      if (!this.updatedValue) return true;

      const inOptions = this.options.some(o => o.value === this.updatedValue);
      if (inOptions) return true;

      return this.resolvedVariableChain?.status === 'resolved';
    },
    updatedValueLabel() {
      const selectedOption = this.options.find(
        option => option.value === this.updatedValue
      );
      if (selectedOption) return selectedOption.label;

      const resolved = this.resolvedVariableChain;
      if (resolved?.status === 'resolved') {
        return resolved.chain.map(node => node.resolvedLabel).join('/');
      }

      return this.defaultOptionLabel;
    },
    errorMessage() {
      if (this.v$.updatedValue.required.$invalid) {
        return this.$t('WGPT_AUTOMATIONS.REGISTRY.FORM.ERRORS.REQUIRED');
      }
      if (this.v$.updatedValue.exists.$invalid) {
        return this.$t(
          'WGPT_AUTOMATIONS.REGISTRY.FORM.ERRORS.VARIABLE_NOT_FOUND',
          { variable: this.updatedValue }
        );
      }
      return this.$t('WGPT_AUTOMATIONS.REGISTRY.FORM.ERRORS.INVALID');
    },
    descriptionTooltip() {
      if (!this.description) return null;

      return {
        content: this.toLocale(this.description),
        popperClass:
          'max-w-[300px] max-h-[300px] overflow-y-auto whitespace-pre-line',
        boundary: 'body',
        popperTriggers: ['hover'],
      };
    },
  },
  validations() {
    return {
      updatedValue: {
        required: requiredIf(this.required),
        exists: helpers.withMessage(
          this.$t('WGPT_AUTOMATIONS.REGISTRY.FORM.ERRORS.VARIABLE_NOT_FOUND'),
          () => this.variableExists
        ),
      },
    };
  },
  watch: {
    value(value) {
      this.updatedValue = value ?? '';
    },
  },
  mounted() {
    if (this.dirty) {
      this.$nextTick(() => {
        this.v$.$validate();
      });
    }
  },
  methods: {
    onVariableAdd(_, updatedValue) {
      this.updatedValue = updatedValue;
      this.updateValue();
    },
    updateValue() {
      this.v$.$touch();
      this.$emit('change', this.updatedValue);
    },
    formatSelectValue(value) {
      if (value === '') return null;

      switch (this.valueType) {
        case 'number':
          return Number(value);
        case 'boolean':
          return value === 'true';
        case 'date':
          return new Date(value);
        default:
          return value;
      }
    },
  },
};
</script>

<template>
  <div>
    <div
      class="relative flex flex-col min-w-0 gap-1"
      :class="{ error: v$.updatedValue.$error }"
    >
      <label
        v-if="label || descriptionTooltip"
        class="flex items-center gap-2 text-sm font-medium text-n-slate-12"
      >
        <div v-if="label" class="block truncate">
          {{ toLocale(label, fallbackLabel) }}<span v-if="required">*</span>
        </div>
        <IconNext
          v-if="descriptionTooltip"
          v-tooltip.top="descriptionTooltip"
          icon="i-lucide-info"
          class="text-n-slate-11 size-3.5"
        />
      </label>

      <div
        class="flex relative [&>button]:bg-n-alpha-black2 [&>button:not(.focused)]:dark:outline-n-weak [&>button:not(.focused)]:hover:!outline-n-slate-6"
      >
        <ButtonNext
          variant="outline"
          :color="
            v$.updatedValue.$error
              ? 'ruby'
              : showVariablesDropdown
                ? 'blue'
                : 'slate'
          "
          :label="updatedValueLabel"
          trailing-icon
          :disabled="disabled"
          class="bg-n-alpha-black2 justify-between w-full !px-3 !py-2.5 text-n-slate-12 font-normal hover:!border-n-slate-6"
          :class="{
            focused: showVariablesDropdown,
            '!border-n-ruby-8 !border': v$.updatedValue.$error,
          }"
          :icon="
            showVariablesDropdown
              ? 'i-lucide-chevron-up'
              : 'i-lucide-chevron-down'
          "
          @click="openVariablesDropdown"
        />
      </div>

      <p
        v-if="v$.updatedValue.$error"
        v-tooltip.top="errorMessage"
        class="min-w-0 mt-1 mb-0 text-xs truncate transition-all duration-500 ease-in-out text-n-ruby-9 dark:text-n-ruby-9"
      >
        {{ errorMessage }}
      </p>
    </div>

    <VariablesDropdown
      :show-dropdown="showVariablesDropdown"
      :event="variablesDropdownEvent"
      :variables="variables"
      :selected="updatedValue"
      :filter-by-prefix="variablesFilter"
      @close="closeVariablesDropdown"
      @add="addVariable"
    />
  </div>
</template>
